import { sidebarAnchor, type AnchorSpec } from '../engine/anchors';
import { clickWhenReady, delay, findVisible, isFilled, waitUntil } from './formFill';
import { closeFlowChrome } from './closeChrome';
import type { TaskFlow } from './types';

const SOCIAL_MEDIA_PATH = '/home/social-media/posts';

const SIDEBAR_LINK = sidebarAnchor('Social Media', 'nav-social-media');

const NETWORK_TABS_SELECTOR = '[data-testid="social-posts-network-tabs"]';

const CREATE_POST_BUTTON: AnchorSpec = {
  structural: { within: 'body', match: 'button', labels: ['Create post'] },
};

const TEXT_FORMAT_CHIP: AnchorSpec = {
  structural: { within: '[role="dialog"]', match: 'button', labels: ['Text'] },
};

const CAPTION_SELECTOR = '[role="dialog"] textarea';

const SAVE_DRAFT_BUTTON: AnchorSpec = { selector: '[data-testid="create-post-save-draft"]' };

const DUMMY_CAPTION =
  'Excited to share our latest update! 🎉 Take a look and let us know what you think in the comments.';

const INITIAL_LOAD_TIMEOUT_MS = 10000;

const onSocialMediaListPage = () =>
  window.location.pathname === SOCIAL_MEDIA_PATH && Boolean(document.querySelector(NETWORK_TABS_SELECTOR));

const initialLoadSettled = () => document.querySelectorAll('.animate-pulse').length === 0;

function findNetworkTabButton(labelPrefix: string): HTMLElement | null {
  const container = document.querySelector(NETWORK_TABS_SELECTOR);
  if (!container) return null;
  const want = labelPrefix.trim().toLowerCase();
  const buttons = Array.from(container.querySelectorAll('button')) as HTMLElement[];
  return buttons.find((button) => (button.textContent || '').trim().toLowerCase().startsWith(want)) ?? null;
}

function isFacebookTabActive(): boolean {
  return findNetworkTabButton('facebook')?.getAttribute('data-network-tab-active') === 'true';
}

const composerOpen = () => Boolean(findVisible(CAPTION_SELECTOR));

function isTextFormatActive(): boolean {
  const dialog = document.querySelector('[role="dialog"]');
  if (!dialog) return false;
  const buttons = Array.from(dialog.querySelectorAll('button')) as HTMLElement[];
  const chip = buttons.find((button) => (button.textContent || '').trim().toLowerCase() === 'text');
  return chip?.getAttribute('aria-pressed') === 'true';
}

export const SOCIAL_MEDIA_POST_TASK: TaskFlow = {
  id: 'create-social-media-post',
  stepId: 'social-media',
  cta: 'Try it: create a social media post',
  preparingLabel: 'Opening Social Media…',
  title: 'Create a social media post',
  summary: 'Switch to Facebook, pick Text, write a caption, and save it as a draft — you press the buttons.',
  doneTitle: 'Post drafted',
  doneDescription:
    'Saved to your Facebook tab as a draft — nothing was posted. Reopen it any time from the Draft filter to add media, then Publish or Schedule when you are ready.',

  beforeStart: async () => {
    if (!onSocialMediaListPage()) {
      await clickWhenReady(SIDEBAR_LINK);
      const arrived = await waitUntil(onSocialMediaListPage, 5000);
      if (!arrived) {
        console.warn(
          '[tour-guide] "create-social-media-post": never confirmed arrival on the social media ' +
            'workspace. The next step may resolve the wrong button.',
        );
      }
    }
    const settled = await waitUntil(initialLoadSettled, INITIAL_LOAD_TIMEOUT_MS);
    if (!settled) {
      console.warn(
        '[tour-guide] "create-social-media-post": the workspace was still showing loading ' +
          `placeholders after ${INITIAL_LOAD_TIMEOUT_MS}ms — proceeding anyway, but a draft ` +
          'created from here may race the page\'s own slow initial load.',
      );
    }
  },

  afterEnd: async () => {
    await closeFlowChrome(composerOpen);
  },

  steps: [
    {
      id: 'switch-to-facebook',
      label: 'Facebook tab',
      title: 'Switch to Facebook',
      description:
        'This is every post across your connected networks. Switch to the Facebook tab first — its composer can publish a plain text update with no photo or video required, which is the quickest way to see how this works.',
      anchor: { selector: NETWORK_TABS_SELECTOR },
      side: 'bottom',
      spotlight: true,
      ready: onSocialMediaListPage,
      prepare: async () => {},
      onNext: async () => {
        if (isFacebookTabActive()) return;
        findNetworkTabButton('facebook')?.click();
        await waitUntil(isFacebookTabActive, 1000);
        await delay(200);
      },
      satisfied: isFacebookTabActive,
      waitingHint: 'Press Next to switch to the Facebook tab.',
      optional: true,
    },

    {
      id: 'open-composer',
      label: 'New post',
      title: 'Start a new post',
      description:
        'Create post opens the composer for whichever Facebook Page you have connected. Press Next to open it.',
      anchor: CREATE_POST_BUTTON,
      side: 'bottom',
      spotlight: true,
      ready: () => onSocialMediaListPage() && isFacebookTabActive(),
      prepare: async () => {},
      onNext: async () => {
        if (composerOpen()) return;
        await clickWhenReady(CREATE_POST_BUTTON);
      },
      satisfied: composerOpen,
      rewind: async () => {
        await closeFlowChrome(composerOpen, 'rewind');
      },
      waitingHint: 'Press Next to open the composer.',
    },

    {
      id: 'select-format',
      label: 'Text format',
      title: 'Pick the Text format',
      description:
        'Click "Text". Facebook posts can be a photo, a video, a link, or plain text — Text is the only one of those that needs no media attached.',
      anchor: TEXT_FORMAT_CHIP,
      side: 'left',
      spotlight: true,
      ready: composerOpen,
      prepare: async () => {},
      onNext: async () => {
        if (isTextFormatActive()) return;
        await clickWhenReady(TEXT_FORMAT_CHIP);
      },
      satisfied: isTextFormatActive,
      waitingHint: 'Press Next to switch to the Text format.',
      optional: true,
    },

    {
      id: 'write-caption',
      label: 'Caption',
      title: 'Write the caption',
      description:
        'Filled in for you — edit it if you like. This is the text that goes out with the post; Facebook is the one network here that does not need a photo or video alongside it.',
      anchor: { selector: CAPTION_SELECTOR },
      side: 'left',
      spotlight: true,
      ready: composerOpen,
      fill: [[CAPTION_SELECTOR, DUMMY_CAPTION]],
      satisfied: () => isFilled(CAPTION_SELECTOR),
      waitingHint: 'Type a caption to carry on.',
    },

    {
      id: 'save-draft',
      label: 'Save draft',
      title: 'Save it as a draft',
      description:
        'Click Save Draft. Nothing is posted anywhere — it lands under the Draft filter exactly like a real draft, ready to finish (add media, pick a time) whenever you want to actually publish it.',
      anchor: SAVE_DRAFT_BUTTON,
      side: 'top',
      spotlight: true,
      ready: composerOpen,
      prepare: async () => {},
      onNext: async () => {
        await clickWhenReady(SAVE_DRAFT_BUTTON);
        await waitUntil(() => !composerOpen(), 8000);
      },
      satisfied: () => !composerOpen(),
      waitingHint: 'Press Next to save this as a draft.',
    },
  ],
};
